import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { graphqlRequest, GraphQLRequestError } from './client';

// Minimal, dependency-free data hooks over the fetch transport (client.ts).
//
// `useQuery` runs an operation on mount (and when its key changes), exposing
// { data, loading, error, refetch }. `useMutation` returns an imperative `mutate` plus the
// same status fields. Both read the operator's Bearer token from GqlTokenContext, which the
// AuthProvider supplies — this keeps the gql layer decoupled from the auth implementation
// (no import cycle) while still attaching the token to every request.
//
// A 401 (expired / revoked JWT) from any request means the operator is no longer authenticated.
// Both hooks branch on `GraphQLRequestError.status === 401` and invoke an injected
// `onUnauthorized` callback (supplied by AuthProvider via GqlUnauthorizedProvider) so the auth
// layer can sign the operator out and RequireAuth can redirect to /login — instead of leaving
// the app in a broken half-authenticated state. The callback is injected (not imported) so the
// gql layer stays decoupled from the auth implementation (no import cycle).

/**
 * Supplies the current operator token to the data hooks. AuthProvider wraps the app in this
 * so every GraphQL call is authenticated; defaults to `null` (unauthenticated) for isolated
 * rendering/tests.
 */
const GqlTokenContext = createContext<string | null>(null);

export const GqlTokenProvider = GqlTokenContext.Provider;

export function useGqlToken(): string | null {
  return useContext(GqlTokenContext);
}

/**
 * Injected handler invoked when a GraphQL request fails with HTTP 401 (expired/revoked JWT).
 * AuthProvider supplies `signOut`; defaults to a no-op for isolated rendering/tests.
 */
const GqlUnauthorizedContext = createContext<() => void>(() => {});

export const GqlUnauthorizedProvider = GqlUnauthorizedContext.Provider;

export function useGqlUnauthorized(): () => void {
  return useContext(GqlUnauthorizedContext);
}

/** True when the failure is an auth failure (HTTP 401): the token is expired or revoked. */
function isUnauthorized(error: GraphQLRequestError): boolean {
  return error.status === 401;
}

function toError(cause: unknown): GraphQLRequestError {
  if (cause instanceof GraphQLRequestError) return cause;
  if (cause instanceof Error) return new GraphQLRequestError(cause.message);
  return new GraphQLRequestError('Unexpected error');
}

export interface QueryState<TData> {
  data: TData | null;
  loading: boolean;
  error: GraphQLRequestError | null;
  /** Re-run the operation imperatively (e.g. after a mutation). */
  refetch: () => void;
}

export interface UseQueryOptions {
  /** When false, the query is not executed (e.g. waiting on a route param). */
  enabled?: boolean;
}

/**
 * Run a GraphQL query on mount and whenever `variablesKey` changes. Pass a stable
 * `variablesKey` (e.g. the id) so the effect doesn't re-run on every render.
 */
export function useQuery<TData, TVariables = Record<string, unknown>>(
  query: string,
  variables?: TVariables,
  options: UseQueryOptions = {},
): QueryState<TData> {
  const enabled = options.enabled ?? true;
  const token = useGqlToken();
  const onUnauthorized = useGqlUnauthorized();
  const [data, setData] = useState<TData | null>(null);
  const [loading, setLoading] = useState<boolean>(enabled);
  const [error, setError] = useState<GraphQLRequestError | null>(null);

  // Re-run trigger for refetch(); bump to force the effect.
  const [nonce, setNonce] = useState(0);
  const refetch = useCallback(() => setNonce((n) => n + 1), []);

  // Serialize variables so the effect depends on their value, not identity.
  const variablesKey = JSON.stringify(variables ?? {});

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    graphqlRequest<TData, TVariables>(query, variables, {
      token,
      signal: controller.signal,
    })
      .then((result) => {
        if (controller.signal.aborted) return;
        setData(result);
        setLoading(false);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        const err = toError(cause);
        setError(err);
        setLoading(false);
        // A 401 means the JWT is expired/revoked — force re-auth so RequireAuth redirects
        // to /login rather than leaving the app half-authenticated.
        if (isUnauthorized(err)) onUnauthorized();
      });

    return () => controller.abort();
    // `variablesKey` captures the variables value; `query`/`token`/`nonce`/`enabled` are primitives.
    // `onUnauthorized` is a stable callback from context.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, variablesKey, token, nonce, enabled]);

  return { data, loading, error, refetch };
}

export interface MutationState<TData, TVariables> {
  /** Execute the mutation; resolves with data or rejects with GraphQLRequestError. */
  mutate: (variables: TVariables) => Promise<TData>;
  data: TData | null;
  loading: boolean;
  error: GraphQLRequestError | null;
  /** Clear any prior result/error (e.g. when reopening a form). */
  reset: () => void;
}

/**
 * Imperative GraphQL mutation hook. `mutate(variables)` returns the typed data (so callers can
 * read e.g. the one-time `rawKey`) and updates loading/error/data state. Guards against state
 * updates after unmount.
 */
export function useMutation<TData, TVariables = Record<string, unknown>>(
  mutation: string,
): MutationState<TData, TVariables> {
  const token = useGqlToken();
  const onUnauthorized = useGqlUnauthorized();
  const [data, setData] = useState<TData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<GraphQLRequestError | null>(null);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setLoading(false);
  }, []);

  const mutate = useCallback(
    async (variables: TVariables): Promise<TData> => {
      setLoading(true);
      setError(null);
      try {
        const result = await graphqlRequest<TData, TVariables>(mutation, variables, {
          token,
        });
        if (mountedRef.current) {
          setData(result);
          setLoading(false);
        }
        return result;
      } catch (cause: unknown) {
        const err = toError(cause);
        if (mountedRef.current) {
          setError(err);
          setLoading(false);
        }
        // A 401 means the JWT is expired/revoked — force re-auth. Fire regardless of mount
        // state: signing out is a global concern, not tied to this component still being alive.
        if (isUnauthorized(err)) onUnauthorized();
        throw err;
      }
    },
    [mutation, token, onUnauthorized],
  );

  return { mutate, data, loading, error, reset };
}
